
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download, Eye, EyeOff, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ResumePreviewDialogProps {
  resume: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ResumePreviewDialog = ({ resume, open, onOpenChange }: ResumePreviewDialogProps) => {
  const [showChanges, setShowChanges] = useState(true);
  
  if (!resume) return null;
  
  const getWordCount = (text: string) => {
    return text ? text.split(/\s+/).filter(Boolean).length : 0;
  };
  
  const fileType = resume.original_filename ? 
    resume.original_filename.split('.').pop()?.toUpperCase() : 
    "TXT";

  const formatChanges = (content: string) => {
    if (!content) return "";
    
    return content
      .split('\n')
      .map((line, i) => {
        if (!showChanges) {
          // If not showing changes, remove the arrow prefix and explanations
          return line.startsWith('→ ') ? 
            `<div class="py-1">${line.substring(2).replace(/\[.*?\]/g, '')}</div>` :
            `<div class="py-1">${line}</div>`;
        }
        
        // Highlight modified lines with a green background
        if (line.startsWith('→ ')) {
          // Extract the explanation in square brackets if it exists
          const explanationMatch = line.match(/\[(.*?)\]$/);
          const explanation = explanationMatch ? explanationMatch[1] : '';
          const textWithoutExplanation = explanation ? 
            line.replace(`[${explanation}]`, '') : line;
            
          return `
            <div class="bg-green-50 border-l-4 border-green-500 pl-3 py-1 my-1 group relative">
              ${textWithoutExplanation}
              ${explanation ? `<span class="text-xs text-green-600 block mt-1 italic">${explanation}</span>` : ''}
            </div>`;
        }
        
        // If it's the keyword section header, make it stand out
        if (line.includes('KEYWORD OPTIMIZATION SUMMARY')) {
          return `<div class="text-lg font-semibold border-b pb-2 mb-4">${line}</div>`;
        }
        
        return `<div class="py-1">${line}</div>`;
      })
      .join('');
  };

  const wordCount = getWordCount(resume.content || "");
  const changesCount = ((resume.content || "").match(/^→ /gm) || []).length;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              <DialogTitle className="text-xl">{resume.title}</DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-gray-100">
                {fileType}
              </Badge>
              {changesCount > 0 && (
                <Badge variant="outline" className="bg-purple-100 text-purple-800">
                  {changesCount} changes
                </Badge>
              )}
              {resume.is_paid ? (
                <Badge variant="outline" className="bg-green-100 text-green-800">
                  Purchased
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                  Preview
                </Badge>
              )}
            </div>
          </div>
          <DialogDescription className="pt-2 flex justify-between items-center">
            <span>
              Created on {new Date(resume.created_at).toLocaleDateString()}
            </span>
            <span className="text-xs text-muted-foreground">
              {wordCount} words
            </span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-3 border-b flex items-center justify-between">
          <Button
            size="sm"
            variant="outline"
            className="flex items-center gap-1"
            onClick={() => setShowChanges(!showChanges)}
          >
            {showChanges ? (
              <>
                <EyeOff className="h-4 w-4" />
                <span>Hide Changes</span>
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                <span>Show Changes</span>
              </>
            )}
          </Button>
          
          <div className="text-xs text-muted-foreground italic flex items-center gap-1">
            <Info className="h-3.5 w-3.5" />
            {changesCount} optimizations made
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto my-2 pr-2">
          {resume.status === 'pending' ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="h-10 w-10 border-t-2 border-blue-500 rounded-full animate-spin mb-4" />
              <h3 className="text-lg font-medium">Processing your resume...</h3>
              <p className="text-muted-foreground mt-2">
                We're optimizing your resume. This may take a moment.
              </p>
            </div>
          ) : resume.content ? (
            <div className="space-y-6">
              <div 
                className="prose max-w-none font-sans text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatChanges(resume.content) }}
              />
              
              {!resume.is_paid && (
                <div className="bg-yellow-50 p-4 rounded-md border border-yellow-100 text-center my-4">
                  <p className="font-medium text-yellow-800">
                    This is a preview of your optimized resume
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Purchase to access all optimization changes and download options
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center p-8 text-muted-foreground">
              No content available for this resume.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResumePreviewDialog;
